import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import {
  isTrackReference,
  useConnectionState,
  useIsSpeaking,
  useLocalParticipant,
  useTracks,
  type TrackReference,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react'
import { ConnectionState, Track } from 'livekit-client'

import type { ChatApi } from './chat/useChat'
import { report } from './diagnostics'
import { wantsCamera, type SaverLevel } from './dataSaver'
import { classifyDeviceError, type DeviceAccess } from './deviceProbe'
import { t } from './i18n'
import { isLiveScreenShare } from './screenShare'
import { isTeacher } from './useClassroomSignals'
import type { PipApi } from './usePictureInPicture'

type AttachableTrack = NonNullable<TrackReference['publication']['track']>

export interface PipStageProps {
  pip: PipApi
  /** Same policy inputs as Stage tiles — the PiP tile must tell the same truth. */
  saverLevel: SaverLevel
  saverKeepStudents: boolean
  isHost: boolean
  handCount: number
  chat: ChatApi
  onOpenRoster: () => void
  onOpenChat: () => void
  onDeviceToggleFailed: (kind: 'mic' | 'camera', access: DeviceAccess) => void
  micFault?: DeviceAccess | null
  credential: { code?: string | null; reconnectToken?: string | null }
}

/**
 * What actually lives inside the floating window.
 *
 * Document mode: a React portal into the PiP window's body. The portal only
 * relocates DOM — context and state flow from Classroom's tree as usual, so
 * this renders live track state with zero extra wiring.
 *
 * The video element is a PLAIN <video> + track.attach, not the
 * components-react <VideoTrack>: in a second document we want full control of
 * attach timing (attach once the element is IN the PiP document — what
 * livekit's isElementInPiP visibility carve-out keys off), and a focus swap
 * (share ends → camera) must detach/reattach explicitly rather than trust a
 * library effect chain we cannot see across documents.
 *
 * Video mode (Android, old Chromium): no custom window exists. We keep ONE
 * offscreen video attached to the focus so the control-bar tap can call
 * requestPictureInPicture with metadata already present — an await between
 * tap and request would spend the gesture's activation. Offscreen = parked
 * outside the viewport, NOT display:none (a non-rendered video may never
 * reach HAVE_METADATA), and at a real size so the PiP frame is not a thumbnail.
 */

export function PipStage({
  pip,
  saverLevel,
  saverKeepStudents,
  isHost,
  handCount,
  chat,
  onOpenRoster,
  onOpenChat,
  onDeviceToggleFailed,
  micFault,
  credential,
}: PipStageProps) {
  const focus = pip.focus

  if (pip.mode === 'document') {
    if (!pip.portalTarget) return null
    return createPortal(
      <>
        <PipTile
          focus={focus}
          saverLevel={saverLevel}
          saverKeepStudents={saverKeepStudents}
          isHost={isHost}
          micFault={micFault}
        />
        <PipControls
          pip={pip}
          isHost={isHost}
          handCount={handCount}
          chat={chat}
          onOpenRoster={onOpenRoster}
          onOpenChat={onOpenChat}
          onDeviceToggleFailed={onDeviceToggleFailed}
          credential={credential}
        />
      </>,
      pip.portalTarget,
    )
  }

  if (pip.mode === 'video') {
    const track =
      focus && isTrackReference(focus) && !focus.publication.isMuted
        ? focus.publication.track
        : undefined
    // While a floating frame is UP, the donor stays mounted even with no
    // track: the hook is about to request an explicit exit, and unmounting
    // first would leave old Safari a frozen frame with no element to settle
    // against. When idle, no track means no donor at all.
    if (!track && !pip.active) return null
    return <PipOffscreenVideo track={track ?? undefined} videoRef={pip.videoRef} />
  }

  return null
}

/**
 * Attach a track to an element with SWAP-SAFE ordering, shared by both video
 * surfaces here.
 *
 * The obvious effect shape — cleanup detaches the old track, next effect
 * attaches the new — has a lethal order for PiP: livekit's detachTrack sets
 * `srcObject = null` when it removes the last track, and Chrome exits element
 * PiP the moment srcObject drops. Attaching FIRST swaps the track inside the
 * element's existing MediaStream (attachToElement removes same-kind tracks
 * and adds the new one), after which detaching the old track is a no-op on
 * the stream and pure adaptiveStream bookkeeping — the floating window never
 * sees an empty element. (GROK-PIP D1, verified against livekit-client 2.21.)
 */
function useSwapAttach(
  track: AttachableTrack | undefined,
  ref: React.RefObject<HTMLVideoElement>,
) {
  const attachedRef = useRef<AttachableTrack | null>(null)
  // The DOM node is CAPTURED at attach time: React nulls ref.current before
  // unmount cleanups run, and reading it then would skip the final detach —
  // leaving a dead element in livekit's attachedElements/elementInfos
  // bookkeeping for the rest of the lesson. (CODEX-PIP D1.)
  const elementRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !track) return // no track: keep the last frame; exit is coming
    elementRef.current = el
    const previous = attachedRef.current
    track.attach(el)
    attachedRef.current = track
    if (previous && previous !== track) previous.detach(el)
  }, [track, ref])

  // Final unmount only: by then the element may live in a closed PiP window —
  // detach is srcObject bookkeeping and safe on dead nodes, but belt-and-braces.
  useEffect(
    () => () => {
      const el = elementRef.current
      const attached = attachedRef.current
      attachedRef.current = null
      elementRef.current = null
      if (el && attached) {
        try {
          attached.detach(el)
        } catch {
          /* dead document — nothing left to clean */
        }
      }
    },
    [],
  )
}

/**
 * The element-PiP donor. Mounted whenever the mode is 'video' and a live
 * focus track exists — before, not after, the user taps the button.
 */
function PipOffscreenVideo({
  track,
  videoRef,
}: {
  track: AttachableTrack | undefined
  videoRef: PipApi['videoRef']
}) {
  useSwapAttach(track, videoRef)
  return <video ref={videoRef} className="pip-offscreen" muted playsInline autoPlay />
}

/** The one tile the floating window shows: video, name, honest empty states. */
function PipTile({
  focus,
  saverLevel,
  saverKeepStudents,
  isHost,
  micFault,
}: {
  focus: TrackReferenceOrPlaceholder | undefined
  saverLevel: SaverLevel
  saverKeepStudents: boolean
  isHost: boolean
  micFault?: DeviceAccess | null
}) {
  // A reconnect must not tear the window down — but a floating window frozen
  // mid-frame with no explanation reads as the class dying. Say what is true.
  const connection = useConnectionState()
  const reconnecting =
    connection === ConnectionState.Reconnecting ||
    connection === ConnectionState.SignalReconnecting

  if (!focus) {
    // Everyone left / no camera published at all: say so rather than sit black.
    return (
      <div className="pip">
        <div className="pip__empty">
          <strong>{t('pip.emptyTitle')}</strong>
          {reconnecting ? t('room.reconnecting') : t('pip.emptyBody')}
        </div>
        <PipStatus isHost={isHost} micFault={micFault} reconnecting={reconnecting} />
      </div>
    )
  }
  return (
    <PipFocusTile
      focus={focus}
      reconnecting={reconnecting}
      saverLevel={saverLevel}
      saverKeepStudents={saverKeepStudents}
      isHost={isHost}
      micFault={micFault}
    />
  )
}

function PipFocusTile({
  focus,
  reconnecting,
  saverLevel,
  saverKeepStudents,
  isHost,
  micFault,
}: {
  focus: TrackReferenceOrPlaceholder
  reconnecting: boolean
  saverLevel: SaverLevel
  saverKeepStudents: boolean
  isHost: boolean
  micFault?: DeviceAccess | null
}) {
  const speaking = useIsSpeaking(focus.participant)
  const name = focus.participant.name || focus.participant.identity
  const isShare = focus.source === Track.Source.ScreenShare

  const publication = isTrackReference(focus) ? focus.publication : undefined
  const hasVideo = Boolean(publication?.track) && !publication?.isMuted
  // Same predicate as the Stage tile, saver policy included: "paused to save
  // data" is only claimed for a CAMERA the saver actually rules out — the
  // saver never touches shares, and a transient not-yet-subscribed
  // publication must not pin a policy on itself that is not running.
  const pausedBySaver =
    !hasVideo &&
    !isShare &&
    Boolean(publication) &&
    !publication?.isMuted &&
    saverLevel > 0 &&
    !wantsCamera(saverLevel, isTeacher(focus.participant.identity), saverKeepStudents)

  return (
    <div className={`pip ${speaking ? 'pip--speaking' : ''}`}>
      {hasVideo && publication?.track ? (
        <PipVideo track={publication.track} cover={!isShare} />
      ) : (
        <div className="pip__empty">
          <strong>
            <bdi dir="auto">{name}</bdi>
          </strong>
          {/* A share with no frames yet is LOADING — "camera off" would be a
              lie about a screen, and the saver never pauses shares. */}
          {t(isShare ? 'pip.loading' : pausedBySaver ? 'saver.pausedTile' : 'prejoin.cameraOff')}
        </div>
      )}
      <span className="pip__label">
        <bdi dir="auto">{name}</bdi>
        {isShare ? ` · ${t('room.presenting')}` : ''}
      </span>
      <PipStatus isHost={isHost} micFault={micFault} reconnecting={reconnecting} />
    </div>
  )
}

function PipVideo({ track, cover }: { track: AttachableTrack; cover: boolean }) {
  const ref = useRef<HTMLVideoElement>(null)
  // Element reused across focus swaps, attach-before-detach (see useSwapAttach)
  // — the same live element avoids a black flash on share→camera transitions.
  useSwapAttach(track, ref)

  return (
    <video
      ref={ref}
      className={`pip__video ${cover ? 'pip__video--cover' : ''}`}
      muted
      playsInline
      autoPlay
    />
  )
}

/** Top status strip: reconnecting, mic fault, or presenting. */
function PipStatus({
  isHost,
  micFault,
  reconnecting,
}: {
  isHost: boolean
  micFault?: DeviceAccess | null
  reconnecting: boolean
}) {
  const screenTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: false })
  const presenting = screenTracks.filter(isLiveScreenShare).some((ref) => ref.participant.isLocal)

  if (reconnecting) return <span className="pip__status">{t('room.reconnecting')}</span>
  if (micFault && micFault !== 'ok') {
    return <span className="pip__status pip__status--alert">{t(isHost ? 'room.micBanner.host' : 'room.micBanner.student')}</span>
  }
  if (presenting) return <span className="pip__status">{t('room.presenting')}</span>
  return null
}

/** Controls inside the floating window: mic, camera, hands, chat, back. */
function PipControls({
  pip,
  isHost,
  handCount,
  chat,
  onOpenRoster,
  onOpenChat,
  onDeviceToggleFailed,
  credential,
}: {
  pip: PipApi
  isHost: boolean
  handCount: number
  chat: ChatApi
  onOpenRoster: () => void
  onOpenChat: () => void
  onDeviceToggleFailed: (kind: 'mic' | 'camera', access: DeviceAccess) => void
  credential: { code?: string | null; reconnectToken?: string | null }
}) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant()
  const chatSnap = useSyncExternalStore(chat.subscribe, chat.getSnapshot)
  const unread = chatSnap.unread
  const credentialRef = useRef(credential)
  credentialRef.current = credential

  const pipCtl = (kind: 'mic' | 'camera' | 'hands' | 'chat' | 'back') => {
    report(credentialRef.current, { stage: 'permissions', outcome: 'ok', permission: `pip=ctl:${kind}` })
  }

  const toggleMic = useCallback(() => {
    pipCtl('mic')
    const enabled = !isMicrophoneEnabled
    void localParticipant?.setMicrophoneEnabled(enabled).catch((error) => {
      if (enabled) onDeviceToggleFailed('mic', classifyDeviceError(error).access)
    })
  }, [isMicrophoneEnabled, localParticipant, onDeviceToggleFailed])

  const toggleCam = useCallback(() => {
    pipCtl('camera')
    const enabled = !isCameraEnabled
    void localParticipant?.setCameraEnabled(enabled).catch((error) => {
      if (enabled) onDeviceToggleFailed('camera', classifyDeviceError(error).access)
    })
  }, [isCameraEnabled, localParticipant, onDeviceToggleFailed])

  const handleHands = useCallback(() => {
    pipCtl('hands')
    pip.toggle()
    onOpenRoster()
  }, [pip, onOpenRoster])

  const handleChat = useCallback(() => {
    pipCtl('chat')
    pip.toggle()
    onOpenChat()
  }, [pip, onOpenChat])

  const handleBack = useCallback(() => {
    pipCtl('back')
    pip.toggle()
  }, [pip])

  return (
    <div className="pip__controls" role="toolbar" aria-label={t('room.controls')}>
      <button
        type="button"
        className={isMicrophoneEnabled ? '' : 'danger'}
        onClick={toggleMic}
        aria-label={t(isMicrophoneEnabled ? 'prejoin.micOn' : 'prejoin.micOff')}
        aria-pressed={isMicrophoneEnabled}
      >
        🎤
      </button>
      <button
        type="button"
        className={isCameraEnabled ? '' : 'danger'}
        onClick={toggleCam}
        aria-label={t(isCameraEnabled ? 'prejoin.camOn' : 'prejoin.camOff')}
        aria-pressed={isCameraEnabled}
      >
        📷
      </button>

      {isHost ? (
        <button
          type="button"
          className={handCount > 0 ? 'accent' : ''}
          onClick={handleHands}
          aria-label={t('room.handsUp').replace('{count}', String(handCount))}
          disabled={handCount === 0}
        >
          ✋
          {handCount > 0 ? <span className="count" aria-hidden="true">{handCount}</span> : null}
        </button>
      ) : null}

      <button
        type="button"
        onClick={handleChat}
        aria-label={unread > 0 ? t('chat.openUnread').replace('{count}', String(unread)) : t('chat.open')}
      >
        💬
        {unread > 0 ? <span className="count" aria-hidden="true">{unread > 99 ? '99+' : unread}</span> : null}
      </button>

      <button
        type="button"
        onClick={handleBack}
        aria-label={t('pip.exit')}
      >
        ⤴
      </button>
    </div>
  )
}
